import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { api } from 'common';
import {
  isStayPartnerType, INVENTORY_TYPES, accentColorForType,
} from 'common/src/other/PartnerUtils';
import {
  Box, Typography, Stack, Paper, Button, IconButton, Switch,
  Dialog, DialogTitle, DialogContent, DialogActions, TextField, Grid, Avatar, Chip
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/DeleteOutline';
import AddIcon from '@mui/icons-material/Add';
import PhotoCameraIcon from '@mui/icons-material/PhotoCamera';
import { MAIN_COLOR, FONT_FAMILY } from '../common/sharedFunctions';
import CircularLoading from '../components/CircularLoading';

const ROOM_DEFAULT = {
  inventoryType: INVENTORY_TYPES.ROOM,
  name: '',
  description: '',
  roomType: 'double',
  capacity: { adults: 2, children: 0 },
  bedConfig: '',
  price: 0,
  unitsAvailable: 1,
  active: true,
  images: {},
};

const PRODUCT_DEFAULT = {
  inventoryType: INVENTORY_TYPES.PRODUCT,
  name: '',
  description: '',
  sku: '',
  category: '',
  brand: '',
  price: 0,
  stock: 0,
  unit: 'unit',
  prescriptionRequired: false,
  active: true,
  images: {},
};

export default function PartnerInventory() {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { partnerId: routePartnerId } = useParams();   // /admin/partners/:partnerId/inventory
  const auth = useSelector((s) => s.auth);
  const partnerdata = useSelector((s) => s.partnerdata);
  const inventorydata = useSelector((s) => s.inventorydata);
  const [editing, setEditing] = useState(null);
  const [imageFile, setImageFile] = useState(null);
  const [adminPartner, setAdminPartner] = useState(null);
  const { fetchPartnersByOwner, fetchInventory, editInventoryItem, fetchPartnerById } = api;

  // Modo admin: carga el partner por id de URL (override).
  // Modo socio: carga su propio partner via owner=uid.
  useEffect(() => {
    if (routePartnerId && fetchPartnerById) {
      dispatch(fetchPartnerById(routePartnerId)).then((p) => setAdminPartner(p)).catch(() => {});
    } else if (auth?.profile?.uid) {
      dispatch(fetchPartnersByOwner(auth.profile.uid));
    }
  }, [dispatch, fetchPartnersByOwner, fetchPartnerById, auth?.profile?.uid, routePartnerId]);

  const myPartner = routePartnerId ? adminPartner : (partnerdata.partners || [])[0];

  useEffect(() => {
    if (myPartner?.id) dispatch(fetchInventory(myPartner.id));
  }, [dispatch, fetchInventory, myPartner?.id]);

  const isStay = myPartner ? isStayPartnerType(myPartner.partnerType) : false;
  const items = myPartner ? (inventorydata.byPartnerId?.[myPartner.id] || []) : [];

  const onAdd = () => {
    setEditing(isStay ? { ...ROOM_DEFAULT, partnerId: myPartner.id } : { ...PRODUCT_DEFAULT, partnerId: myPartner.id });
    setImageFile(null);
  };

  const onEdit = (item) => {
    setEditing({ ...item, partnerId: myPartner.id });
    setImageFile(null);
  };

  const onSave = async () => {
    let id = editing.id;
    if (!id) {
      id = await dispatch(editInventoryItem(editing, 'Add'));
    } else {
      await dispatch(editInventoryItem(editing, 'Update'));
    }
    if (imageFile && id) {
      const idx = Object.keys(editing.images || {}).length;
      await dispatch(editInventoryItem({
        id, partnerId: myPartner.id, imageIndex: idx, imageFile,
      }, 'AddImage'));
    }
    setEditing(null);
    setImageFile(null);
  };

  const onDelete = async (item) => {
    if (window.confirm(t('delete_confirm') || 'Eliminar este item?')) {
      await dispatch(editInventoryItem({ id: item.id, partnerId: myPartner.id }, 'Delete'));
    }
  };

  const update = (path, value) => {
    setEditing((prev) => {
      const next = { ...prev };
      const segs = path.split('.');
      let cur = next;
      for (let i = 0; i < segs.length - 1; i += 1) {
        cur[segs[i]] = { ...(cur[segs[i]] || {}) };
        cur = cur[segs[i]];
      }
      cur[segs[segs.length - 1]] = value;
      return next;
    });
  };

  if (!myPartner) {
    return (
      <Box sx={{ p: 4, fontFamily: FONT_FAMILY }}>
        <Typography>{t('create_business_first') || 'Crea tu negocio primero.'}</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ p: { xs: 1.5, md: 3 }, fontFamily: FONT_FAMILY }}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
        <Stack>
          <Typography variant="h5" sx={{ fontWeight: 700 }}>
            {isStay ? (t('rooms') || 'Habitaciones') : (t('products') || 'Productos')}
          </Typography>
          <Chip
            size="small" label={myPartner.partnerType}
            sx={{
              alignSelf: 'flex-start', mt: 0.5,
              backgroundColor: `${accentColorForType(myPartner.partnerType)}1a`,
              color: accentColorForType(myPartner.partnerType),
              fontWeight: 600,
            }}
          />
        </Stack>
        <Button variant="contained" startIcon={<AddIcon />} sx={{ backgroundColor: MAIN_COLOR }} onClick={onAdd}>
          {t('add') || 'Añadir'}
        </Button>
      </Stack>

      <Stack spacing={1.25}>
        {items.map((it) => (
          <Paper key={it.id} variant="outlined" sx={{ p: 1.5, borderRadius: 3 }}>
            <Stack direction="row" alignItems="center" spacing={1.5}>
              <Avatar variant="rounded" src={it.images && Object.values(it.images)[0]} sx={{ width: 64, height: 64 }}>
                {(it.name || '?').slice(0, 1)}
              </Avatar>
              <Box sx={{ flex: 1 }}>
                <Typography sx={{ fontWeight: 600 }}>{it.name}</Typography>
                <Typography variant="caption" color="text.secondary">
                  {isStay
                    ? `${it.capacity?.adults || 1} ${t('adults') || 'adultos'} · ${it.unitsAvailable || 1} ${t('units') || 'unidades'}`
                    : `${it.category || ''} · ${it.stock || 0} ${t('in_stock') || 'en stock'}`}
                </Typography>
              </Box>
              <Typography sx={{ fontWeight: 700 }}>
                {myPartner.currency} {Number(it.price || 0).toFixed(2)}
              </Typography>
              <Switch
                checked={it.active !== false}
                onChange={() => dispatch(editInventoryItem({ ...it, partnerId: myPartner.id, active: !it.active }, 'Update'))}
              />
              <IconButton onClick={() => onEdit(it)}><EditIcon /></IconButton>
              <IconButton color="error" onClick={() => onDelete(it)}><DeleteIcon /></IconButton>
            </Stack>
          </Paper>
        ))}
        {items.length === 0 && (
          <Typography color="text.secondary" sx={{ py: 4, textAlign: 'center' }}>
            {t('no_inventory') || 'Sin items aún.'}
          </Typography>
        )}
      </Stack>

      <Dialog open={!!editing} onClose={() => setEditing(null)} maxWidth="sm" fullWidth>
        <DialogTitle>{editing?.id ? (t('edit') || 'Editar') : (t('add') || 'Añadir')}</DialogTitle>
        <DialogContent>
          {editing && (
            <Grid container spacing={2} sx={{ mt: 0 }}>
              <Grid item xs={12}>
                <TextField fullWidth size="small" label={t('name') || 'Nombre'} value={editing.name}
                  onChange={(e) => update('name', e.target.value)} />
              </Grid>
              <Grid item xs={12}>
                <TextField fullWidth size="small" label={t('description') || 'Descripción'}
                  multiline rows={2} value={editing.description}
                  onChange={(e) => update('description', e.target.value)} />
              </Grid>
              <Grid item xs={6}>
                <TextField fullWidth size="small" type="number" label={t('price') || 'Precio'} value={editing.price}
                  onChange={(e) => update('price', parseFloat(e.target.value) || 0)} />
              </Grid>
              {isStay ? (
                <>
                  <Grid item xs={6}>
                    <TextField fullWidth size="small" type="number" label={t('units_available') || 'Unidades disponibles'}
                      value={editing.unitsAvailable}
                      onChange={(e) => update('unitsAvailable', parseInt(e.target.value, 10) || 1)} />
                  </Grid>
                  <Grid item xs={6}>
                    <TextField fullWidth size="small" type="number" label={t('adults') || 'Adultos'}
                      value={editing.capacity?.adults || 1}
                      onChange={(e) => update('capacity.adults', parseInt(e.target.value, 10) || 1)} />
                  </Grid>
                  <Grid item xs={6}>
                    <TextField fullWidth size="small" type="number" label={t('children') || 'Niños'}
                      value={editing.capacity?.children || 0}
                      onChange={(e) => update('capacity.children', parseInt(e.target.value, 10) || 0)} />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField fullWidth size="small" label={t('bed_config') || 'Configuración de camas'}
                      value={editing.bedConfig} onChange={(e) => update('bedConfig', e.target.value)} />
                  </Grid>
                </>
              ) : (
                <>
                  <Grid item xs={6}>
                    <TextField fullWidth size="small" type="number" label={t('stock') || 'Stock'}
                      value={editing.stock}
                      onChange={(e) => update('stock', parseInt(e.target.value, 10) || 0)} />
                  </Grid>
                  <Grid item xs={6}>
                    <TextField fullWidth size="small" label={t('sku') || 'SKU'} value={editing.sku || ''}
                      onChange={(e) => update('sku', e.target.value)} />
                  </Grid>
                  <Grid item xs={6}>
                    <TextField fullWidth size="small" label={t('category') || 'Categoría'} value={editing.category || ''}
                      onChange={(e) => update('category', e.target.value)} />
                  </Grid>
                  <Grid item xs={6}>
                    <TextField fullWidth size="small" label={t('brand') || 'Marca'} value={editing.brand || ''}
                      onChange={(e) => update('brand', e.target.value)} />
                  </Grid>
                </>
              )}
              <Grid item xs={12}>
                <Button component="label" startIcon={<PhotoCameraIcon />} variant="outlined" size="small">
                  {t('add_image') || 'Añadir imagen'}
                  <input hidden type="file" accept="image/*" onChange={(e) => setImageFile(e.target.files?.[0] || null)} />
                </Button>
                {imageFile && (
                  <Typography variant="caption" sx={{ ml: 1 }}>{imageFile.name}</Typography>
                )}
              </Grid>
            </Grid>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditing(null)}>{t('cancel') || 'Cancelar'}</Button>
          <Button variant="contained" sx={{ backgroundColor: MAIN_COLOR }} onClick={onSave} disabled={!editing?.name}>
            {t('save') || 'Guardar'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
